/**
 * Reading and changing scopes of service, through the callables.
 *
 * Everything goes through Cloud Functions: the rules allow members to read
 * the collection, but the list callable is what shapes the rows, and every
 * write is server-side (firestore.rules: `allow write: if false`). One
 * mutation hook, parameterised by callable name, because the six status
 * actions are the same shape — an id in, a success flag out, the detail and
 * the list to refetch afterwards.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call } from '../../lib/callable';
import {
  toScopeStatus,
  type ScopeDetail,
  type ScopeEvent,
  type ScopeFields,
  type ScopeSummary,
  type ScopeVersion, toAgeGroups } from './types';

const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const strs = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

function toSummary(d: Record<string, unknown>): ScopeSummary {
  return {
    id: String(d.id ?? ''),
    unitName: str(d.unitName) ?? '(unnamed unit)',
    unitCode: str(d.unitCode) ?? '',
    departmentId: str(d.departmentId),
    facilityNames: strs(d.facilityNames),
    status: toScopeStatus(d.status),
    version: typeof d.version === 'number' ? d.version : 1,
    effectiveDate: str(d.effectiveDate),
    nextReviewDate: str(d.nextReviewDate),
    updatedAt: str(d.updatedAt),
  };
}

function toDetail(d: Record<string, unknown>): ScopeDetail {
  return {
    ...toSummary(d),
    description: str(d.description),
    patientPopulations: strs(d.patientPopulations),
    servicesProvided: strs(d.servicesProvided),
    exclusionsLimitations: str(d.exclusionsLimitations),
    hoursOfOperation: str(d.hoursOfOperation),
    staffingRequirements: str(d.staffingRequirements),
    equipmentRequirements: str(d.equipmentRequirements),
    ageGroups: toAgeGroups(d.ageGroups),
    bedCount: typeof d.bedCount === 'number' && Number.isFinite(d.bedCount) ? d.bedCount : null,
    physicalLocation: str(d.physicalLocation),
    unitLeader: str(d.unitLeader),
    medicalDirector: str(d.medicalDirector),
    qualityIndicators: strs(d.qualityIndicators),
    createdAt: str(d.createdAt),
  };
}

export const scopesKey = (orgId: string | null) => ['scopes', orgId] as const;
export const scopeKey = (orgId: string | null, scopeId: string | null) =>
  ['scope', orgId, scopeId] as const;

export function useScopes(orgId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: scopesKey(orgId),
    enabled: Boolean(orgId) && enabled,
    queryFn: async () => {
      const res = await call<Record<string, never>, { scopes: Record<string, unknown>[] }>('listScopes', {});
      return (res.scopes ?? []).map(toSummary);
    },
  });
}

export interface ScopeDetailResult {
  scope: ScopeDetail;
  versions: ScopeVersion[];
  events: ScopeEvent[];
}

export function useScopeDetail(orgId: string | null, scopeId: string | null, enabled = true) {
  return useQuery({
    queryKey: scopeKey(orgId, scopeId),
    enabled: Boolean(orgId && scopeId) && enabled,
    queryFn: async (): Promise<ScopeDetailResult> => {
      const res = await call<{ scopeId: string }, {
        scope: Record<string, unknown>;
        versions: Record<string, unknown>[];
        events: Record<string, unknown>[];
      }>('getScopeDetail', { scopeId: scopeId! });
      return {
        scope: toDetail(res.scope),
        versions: (res.versions ?? []).map((v) => ({
          id: String(v.id ?? ''),
          version: typeof v.version === 'number' ? v.version : 0,
          createdAt: str(v.createdAt),
          changeNotes: str(v.changeNotes),
        })),
        events: (res.events ?? []).map((e) => ({
          id: String(e.id ?? ''),
          action: str(e.action) ?? '',
          actorEmail: str(e.actorEmail),
          details: typeof e.details === 'object' && e.details !== null ? (e.details as Record<string, unknown>) : null,
          createdAt: str(e.createdAt),
        })),
      };
    },
  });
}

/**
 * What the create and update callables are sent.
 *
 * Empty text is sent as empty, so clearing a field on edit clears it on the
 * server. A date or department that is not set is LEFT OUT, not sent as
 * undefined: the callable SDK encodes undefined as null on the wire, and the
 * backend's `.optional()` accepts a missing key but refuses null — found on
 * the first real save, which the mocked tests could not see.
 */
export function toPayload(fields: ScopeFields): Record<string, unknown> {
  const iso = (d: string) => (/^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T12:00:00.000Z` : undefined);
  const payload: Record<string, unknown> = {
    unitName: fields.unitName.trim(),
    departmentId: fields.departmentId ?? undefined,
    description: fields.description.trim(),
    patientPopulations: fields.patientPopulations,
    servicesProvided: fields.servicesProvided,
    exclusionsLimitations: fields.exclusionsLimitations.trim(),
    hoursOfOperation: fields.hoursOfOperation.trim(),
    staffingRequirements: fields.staffingRequirements.trim(),
    equipmentRequirements: fields.equipmentRequirements.trim(),
    ageGroups: fields.ageGroups,
    bedCount: /^\d+$/.test(fields.bedCount.trim()) ? Number(fields.bedCount) : undefined,
    physicalLocation: fields.physicalLocation.trim(),
    unitLeader: fields.unitLeader.trim(),
    medicalDirector: fields.medicalDirector.trim(),
    qualityIndicators: fields.qualityIndicators,
    effectiveDate: iso(fields.effectiveDate),
    nextReviewDate: iso(fields.nextReviewDate),
  };
  for (const key of Object.keys(payload)) if (payload[key] === undefined) delete payload[key];
  return payload;
}

export function useCreateScope(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: ScopeFields) => {
      const res = await call<unknown, { scopeId: string }>('createScopeOfService', {
        ...toPayload(fields),
        unitCode: fields.unitCode.trim(),
      });
      return res.scopeId;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: scopesKey(orgId) }),
  });
}

export function useUpdateScope(orgId: string | null, scopeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fields: ScopeFields) => {
      await call<unknown, { success: boolean }>('updateScopeOfService', { scopeId, ...toPayload(fields) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scopesKey(orgId) });
      void qc.invalidateQueries({ queryKey: scopeKey(orgId, scopeId) });
    },
  });
}

export type ScopeAction =
  | { name: 'submitScopeForReview' }
  | { name: 'approveScope' }
  | { name: 'rejectScope'; reason: string }
  | { name: 'retireScope' }
  | { name: 'createNewVersion'; changeNotes: string };

/** One hook for the six status changes: the id goes in, the pages refetch. */
export function useScopeAction(orgId: string | null, scopeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action: ScopeAction) => {
      const { name, ...rest } = action;
      const data: Record<string, unknown> = { scopeId };
      if ('reason' in rest) data.reason = rest.reason.trim();
      if ('changeNotes' in rest && rest.changeNotes.trim()) data.changeNotes = rest.changeNotes.trim();
      await call<unknown, { success: boolean }>(name, data);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: scopesKey(orgId) });
      void qc.invalidateQueries({ queryKey: scopeKey(orgId, scopeId) });
    },
  });
}

export interface ImportResult {
  created: { row: number; unitCode: string; unitName: string; scopeId: string }[];
  failed: { row: number; unitCode: string; errors: string[] }[];
}

export function useImportScopes(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (csvContent: string) =>
      call<{ csvContent: string }, ImportResult>('importScopesFromCsv', { csvContent }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: scopesKey(orgId) }),
  });
}
